<script>
    import {suddenDeathPlayers} from '../store';
    import {createEventDispatcher, onMount} from "svelte";
    import {HistoryEventType} from "../history-events.enum";
    import Toast from "svelma/src/components/Toast";
    import {SuddenDeathHistoryEvent} from "../suddendeath.model";
    import {HistoryEvent} from "../dealadart.model";

    const dispatch = createEventDispatcher();

    let rounds = Array.from(new Array(Math.max(1, $suddenDeathPlayers.length - 1)), (x, i) => i + 1);
    let gameFinished = false;
    let activePlayerIndex = 0;
    let history = [];
    let scoreInputElement;
    let playerScore = null;
    let currentRound = 0;

    function reset() {
        gameFinished = false;
        currentRound = 0;
        activePlayerIndex = 0;
        history = [];
        playerScore = null;

        dispatch('reset');
        focusInput();
    }

    function newGame() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        dispatch('newGame');
    }

    function handleScore(scoreVal) {
        if (gameFinished) return;

        let numScore = scoreVal == null || scoreVal === '' ? 0 : Number(scoreVal);

        if (isNaN(numScore)) {
            numScore = 0;
        }

        if (numScore > 180) {
            Toast.create({message: 'More than maximum entered (180)!', type: 'is-danger'});
            return;
        }

        let activePlayer = $suddenDeathPlayers[activePlayerIndex];
        if (!activePlayer) return;

        let targetRoundNum = parseInt(rounds[currentRound]);
        let roundData = activePlayer.rounds.filter(item => item.round === targetRoundNum)[0];
        if (roundData) {
            roundData.score = numScore;
        }

        history = [
            ...history,
            new SuddenDeathHistoryEvent(
                HistoryEventType.POINTS_ADDED,
                targetRoundNum,
                numScore,
                activePlayer.eliminated,
                activePlayerIndex
            )
        ];

        activePlayer.rounds = [...activePlayer.rounds];

        eliminatePlayer();

        suddenDeathPlayers.update(players => {
            if (players[activePlayerIndex]) {
                players[activePlayerIndex].rounds = [...players[activePlayerIndex].rounds];
            }
            return [...players];
        });

        checkWinner();
        if (!gameFinished) {
            nextPlayer();
        } else {
            playerScore = null;
        }

        focusInput();
    }

    function eliminatePlayer() {
        let activePlayers = $suddenDeathPlayers.filter(player => !player.eliminated);
        let indexOfLastActivePlayer = $suddenDeathPlayers.indexOf(activePlayers[activePlayers.length - 1]);
        if (indexOfLastActivePlayer === activePlayerIndex) {
            let lowestScore;
            let playerToEliminate;
            activePlayers.forEach(player => {
                let suddenDeathPlayerRound = player.rounds.filter(item => item.round === currentRound + 1)[0];
                if (suddenDeathPlayerRound && (lowestScore === undefined || suddenDeathPlayerRound.score < lowestScore)) {
                    lowestScore = suddenDeathPlayerRound.score;
                    playerToEliminate = player;
                }
            });

            if (playerToEliminate) {
                let suddenDeathPlayerElement = activePlayers.find(player => player.name === playerToEliminate.name);
                if (suddenDeathPlayerElement) {
                    suddenDeathPlayerElement.eliminated = true;
                }
            }
        }
    }

    function checkWinner() {
        let remainingPlayers = $suddenDeathPlayers.filter(player => !player.eliminated);
        if (remainingPlayers.length === 1) {
            gameFinished = true;
        }
    }

    function nextPlayer() {
        if (activePlayerIndex === $suddenDeathPlayers.length - 1) {
            activePlayerIndex = 0;
            currentRound = currentRound + 1;
            if ($suddenDeathPlayers[activePlayerIndex]?.eliminated) {
                nextPlayer();
                return;
            }
        } else {
            activePlayerIndex = activePlayerIndex + 1;

            if ($suddenDeathPlayers[activePlayerIndex]?.eliminated) {
                nextPlayer();
                return;
            }
        }
        playerScore = null;

        history = [...history, new HistoryEvent(HistoryEventType.NEXT_PLAYER, activePlayerIndex, 0)];
        focusInput();
    }

    function undo() {
        gameFinished = false;
        let lastHistoryItem = history[history.length - 1];

        if (!lastHistoryItem) {
            return;
        }
        let player = $suddenDeathPlayers[lastHistoryItem.playerIndex];

        if (lastHistoryItem.type === HistoryEventType.POINTS_ADDED) {
            if (player) {
                player.eliminated = false;
                let round = lastHistoryItem.round;
                let roundSegment = player.rounds.filter(item => item.round === round)[0];
                if (roundSegment) {
                    roundSegment.score = null;
                }
            }
        }

        history = history.slice(0, -1);
        suddenDeathPlayers.update(players => {
            if (players[activePlayerIndex]) {
                players[activePlayerIndex].rounds = [...players[activePlayerIndex].rounds];
            }
            return [...players];
        });

        if (lastHistoryItem.type === HistoryEventType.NEXT_PLAYER) {
            if (activePlayerIndex - 1 < 0) {
                if (currentRound > 0) {
                    activePlayerIndex = $suddenDeathPlayers.length - 1;
                    currentRound = currentRound - 1;
                }
            } else {
                activePlayerIndex = activePlayerIndex - 1;
            }

            undo();
        }

        focusInput();
    }

    function onEnterScore(e) {
        if (e.key === 'Enter' || e.charCode === 13 || e.keyCode === 13) {
            e.preventDefault();
            handleScore(playerScore);
        }
    }

    function focusInput() {
        if (scoreInputElement && !gameFinished) {
            setTimeout(() => {
                scoreInputElement?.focus();
            }, 10);
        }
    }

    function getRoundScore(player, round) {
        const r = player.rounds.find((item) => item.round === round);
        if (!r || r.score == null) {
            return null;
        }
        return r.score;
    }

    function isPlayerActive(player, finished = gameFinished, activeIdx = activePlayerIndex) {
        return !finished && $suddenDeathPlayers[activeIdx]?.name === player?.name;
    }

    function isPlayerWinner(player, finished = gameFinished) {
        if (!finished) return false;
        const winner = $suddenDeathPlayers.find(p => !p.eliminated);
        return winner?.name === player?.name;
    }

    onMount(() => {
        focusInput();
    });
</script>

<div class="suddendeath-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border bg-[#f0ece9]">
    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-sm px-4 sm:px-6 py-3 mb-3 sm:mb-4 border border-black/5 flex flex-wrap items-center justify-between gap-4 shrink-0">
        <!-- Brand & Game Badge -->
        <div class="flex items-center gap-4 sm:gap-6 min-w-[200px]">
            <button class="inline-flex items-center transition-opacity hover:opacity-85 focus:outline-none" on:click={newGame} title="Yuma Home">
                <img alt="Yuma Logo" class="h-8 sm:h-9 w-auto object-contain" src="images/yuma.png" />
            </button>
            <div class="h-8 w-px bg-slate-200"></div>
        </div>

        <!-- Center Controls: Score Input & Next Player -->
        <div class="flex items-center justify-center flex-1 max-w-xl mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 w-full max-w-md bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
                <div class="relative flex-1">
                    <input
                        bind:this={scoreInputElement}
                        bind:value={playerScore}
                        class="w-full h-11 bg-white text-slate-900 text-lg font-bold pl-4 pr-10 rounded-xl border border-slate-200 placeholder-slate-400 focus:border-[#21e467] focus:ring-2 focus:ring-[#21e467]/30 transition-all text-center tracking-wide outline-none disabled:opacity-50"
                        disabled={gameFinished}
                        max="180"
                        min="0"
                        placeholder="Score..."
                        type="number"
                        on:keydown={onEnterScore}
                    />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold select-none pointer-events-none">PTS</span>
                </div>

                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#eba9ff] hover:bg-[#e493fb] active:scale-[0.98] text-[#005d46] font-bold px-5 sm:px-6 rounded-xl transition-all shadow-sm border border-purple-300/30 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={gameFinished}
                    on:click={() => handleScore(playerScore)}
                    type="button"
                    title="Submit score (shortcut: Enter)"
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[200px]">
            <!-- Undo Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={history.length === 0}
                on:click={undo}
                title="Undo last action"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                on:click={reset}
                title="Reset game"
                type="button"
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                on:click={newGame}
                title="Exit to Setup"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: Main Scoreboard Table -->
    <main class="w-full max-w-[1600px] mx-auto flex-1 flex flex-col min-h-0 bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-black/5 overflow-hidden">
        <div class="w-full h-full flex-1 flex flex-col min-h-0 overflow-x-auto">
            <table class="suddendeath-table w-full h-full flex-1 border-collapse select-none">
                <!-- THEAD: Players Header -->
                <thead>
                    <tr class="h-12 sm:h-14 text-white text-lg">
                        <!-- Left Header: Game Title -->
                        <th class="w-[20%] min-w-[160px] bg-[#005d46] px-5 py-2.5 align-middle text-left font-bold text-lg sm:text-xl tracking-tight border-r border-emerald-900/30 text-white" scope="col" style="text-align: left;">
                            <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">local_fire_department</span>
                                <div class="text-left">
                                    <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                        Sudden Death
                                    </div>
                                    <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                        Lowest score is out
                                    </div>
                                </div>
                            </div>
                        </th>

                        <!-- Player Columns -->
                        {#each $suddenDeathPlayers as player, idx}
                            <th
                                class="px-4 py-2.5 text-center align-middle transition-colors border-r last:border-r-0 {
                                    isPlayerWinner(player, gameFinished)
                                        ? 'bg-[#21e467] text-[#005d46] border-emerald-600/40 shadow-sm'
                                        : isPlayerActive(player, gameFinished, activePlayerIndex)
                                            ? 'bg-[#fff8e7] text-amber-950 border-amber-200/80 shadow-inner'
                                            : 'bg-[#005d46] text-white border-emerald-900/30'
                                }"
                                scope="col"
                                style="width: {80 / ($suddenDeathPlayers.length || 1)}%;"
                            >
                                <div class="flex items-center justify-center gap-2 flex-wrap">
                                    {#if isPlayerActive(player, gameFinished, activePlayerIndex) && !isPlayerWinner(player, gameFinished)}
                                        <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                    {/if}
                                    <span class="text-xl sm:text-2xl font-bold font-headline truncate {player.eliminated && !isPlayerWinner(player, gameFinished) ? 'line-through opacity-60 text-rose-300' : ''}">
                                        {player?.name}
                                    </span>
                                    {#if isPlayerWinner(player, gameFinished)}
                                        <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                            🏆
                                        </span>
                                    {/if}
                                </div>
                            </th>
                        {/each}
                    </tr>
                </thead>

                <!-- TBODY: Round Rows -->
                <tbody>
                    {#each rounds as round, roundIdx}
                        <tr class="round-row transition-colors border-b last:border-b-0 border-slate-200/80 {
                            roundIdx === currentRound && !gameFinished
                                ? 'bg-[#fef0cd]/30 hover:bg-[#fef0cd]/50'
                                : roundIdx % 2 === 0
                                ? 'bg-white hover:bg-[#f5f3f0]'
                                : 'bg-[#fbfaf8] hover:bg-[#f5f3f0]'
                        }">
                            <!-- Left: Round Label Column -->
                            <td class="round-label text-left pl-6 sm:pl-8 py-1 sm:py-2 align-middle font-bold border-r border-slate-200 font-headline {
                                roundIdx === currentRound && !gameFinished ? 'text-emerald-900' : 'text-slate-600'
                            }">
                                <div class="flex items-center gap-2.5 h-full min-h-[32px] sm:min-h-[38px]">
                                    {#if roundIdx === currentRound && !gameFinished}
                                        <span class="w-3 h-3 rounded-full bg-[#21e467] border-2 border-emerald-600 shrink-0"></span>
                                    {/if}
                                    <span class="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-none font-headline">
                                        Round {round}
                                    </span>
                                </div>
                            </td>

                            <!-- Players' Round Scores -->
                            {#each $suddenDeathPlayers as player}
                                <td
                                    class="py-1 sm:py-2 align-middle text-center border-r last:border-r-0 border-slate-200/50 transition-all {
                                        roundIdx === currentRound && isPlayerActive(player, gameFinished, activePlayerIndex) && !gameFinished
                                            ? 'bg-[#fef0cd]/70 border-r border-amber-300/80 font-bold text-amber-950'
                                            : player.eliminated && !isPlayerWinner(player, gameFinished)
                                                ? 'opacity-40'
                                                : ''
                                    }"
                                >
                                    <div class="flex items-center justify-center w-full h-full min-h-[32px] sm:min-h-[38px]">
                                        {#if isPlayerActive(player, gameFinished, activePlayerIndex) && roundIdx === currentRound && !gameFinished && getRoundScore(player, round) == null}
                                            <div class="inline-flex items-center justify-center px-3 sm:px-4 py-1 rounded-lg border-2 border-dashed border-amber-400/80 text-amber-800 text-xs sm:text-sm font-semibold animate-pulse box-border">
                                                Throwing...
                                            </div>
                                        {:else if getRoundScore(player, round) != null}
                                            <div class="inline-flex items-center justify-center gap-1.5 font-bold">
                                                <span class="text-xl sm:text-2xl md:text-3xl leading-none text-slate-900 font-black font-headline">
                                                    {getRoundScore(player, round)}
                                                </span>
                                            </div>
                                        {:else}
                                            <span class="text-slate-300 font-bold text-lg sm:text-xl leading-none select-none">-</span>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Footer Info Strip -->
        <div class="bg-stone-50 px-4 sm:px-6 py-2.5 border-t border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 shrink-0">
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[16px] text-[#005d46]">info</span>
                <span>Enter each player's 3-dart score. The lowest scorer each round is eliminated until 1 winner remains!</span>
            </div>
            <div class="flex items-center gap-3 font-medium">
                <span class="inline-flex items-center gap-1">
                    <kbd class="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-[10px] font-mono shadow-xs">Enter</kbd>
                    <span>Submit Score</span>
                </span>
            </div>
        </div>
    </main>
    <!-- END: Main Scoreboard Table -->
</div>

<style>
    :global(body) {
        margin: 0;
        padding: 0;
        background-color: #f0ece9;
    }

    .suddendeath-table {
        table-layout: fixed;
    }

    .suddendeath-table th,
    .suddendeath-table td {
        vertical-align: middle !important;
    }

    .round-row {
        height: 1%;
    }
</style>
